import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ViewTreeNode } from './models/view.tree-node';
import { BdGraphService } from 'src/lib/graph/graph.service';
import { BdGraph, BdGraphPropertyValueMap, BdGraphNode, BdGraphStateMap } from 'src/lib/graph/graph.model';
import { CameraModel } from './models/camera.model';
import { plainToClass, classToClass } from 'class-transformer';
import { BackgroundModel } from './models/background.model';
import { SynchronizationModel } from '../story/models/synchronization.model';
import { GraphUtils } from 'src/lib/graph/graph-utils';
import { ChapterModel } from '../story/models/chapter.model';
import { calculateLeafStateMap } from 'src/lib/graph/leaf-state';


declare const Human: any;

const graphIdKey = 'objectId';
const graphChildKey = 'objects';

const graphProperties = {
  shown: 'shown',
  renderable: (id) => Human.scene.objects[id].isRenderable(),
  pickable: 'pickable',
  label: (id) => Human.scene.objects[id].displayName,
  expanded: (id) => false
};

const graphPropertySync = {
  shown: 'trueWins',
  renderable: 'falseWins',
  pickable: 'trueWins'
};

const defaultGraphConfig = {
  trim: true
};

const enum queryPropertyEnum {
  Hidden = 'hidden',
  Clipped = 'clipped',
  Disabled = 'disabled'
}

class NodeConfig {
  id: string;
  displayName: string;
}

/*
Data specification for per-chapter modifcations to scene graph to create a user facing view graph
*/
class ChapterViewGraphConfig {
  trim: boolean;
  terminal: string[];
  root: {
    children: string[],
    replace: boolean
  };
  nodes: NodeConfig[];
  connect: {
    [key: string]: {
      replace: boolean;
      children: string[];
    };
  };

  constructor(graphData: any) {
    this.trim = graphData.trim || false;
    this.terminal = graphData.terminal || [];
    this.root = graphData.root || {children: [], replace: false};
    this.connect = graphData.connect || {};
    this.nodes = graphData.nodes || [];
  }
}

export enum EngineEventsEnum {
  Loaded = 'loaded',
  Tick = 'tick',

  AssetsLoadStart = 'assets.load.start',
  AssetsLoadProgress = 'assets.load.progress',
  AssetsLoadFinish = 'assets.load.finish',
  ModulesCreateFinish = 'modules.create.finish',
  ModulesDestroryFinish = 'modules.destroy.finish',
  // ModuleInteractive = 'module.interactive',
  ModulesActivateFinish = 'modules.activate.finish',
  SceneObjectsShown = 'scene.objectsShown',
  SceneMarkersUpdated = 'scene.markers.updated',
  SceneObjectRenderable = 'scene.objectRenderable',
  
  TimelineChaptersActivateFinish = 'timeline.chapters.activate.finish',
  TimelineChaptersActivateStart = 'timeline.chapters.activate.start',

  TimelineScrubbed = 'timeline.scrubbed',
  TimelinePlay = 'timeline.play',
  TimelinePlaying = 'timeline.playing',
  TimelineStop = 'timeline.stop',

  HotspotsPositionUpdated = 'hotspots.positionUpdated'
}


@Injectable({
  providedIn: 'root'
})
export class EngineService {

  // #TODO: [EM] these maps should be in state
  private objectToNodeMap: {[key: string]: string} = {}; // one object id in the engine maps to one node in the view graph
  private nodeToObjectMap: {[key: string]: string[]} = {}; // one node in the view graph maps to one or more engine object ids.
  private sceneGraph: BdGraph<BdGraphNode>;

  /**
   * Maps Human.events from the engine in application observables so that we can subscribe, filter, buffer
   * and more without needing direct knowlege of the Human library across the application.
   */
  private events: any = {};

  private makeObservable(event: EngineEventsEnum) {
    return Observable.create(observer => {
      Human.events.on(event, (params) => {
        observer.next(params);
      });
    });
  }

  constructor(
    private graphService: BdGraphService
  ) {
    this.events = Object.values(EngineEventsEnum)
      .reduce((result, event) => {
        result[event] = this.makeObservable(event);
        return result;
      }, {});
  }

  getCanvas(): any {
    return Human.renderer.canvas.canvas;
  }

  getEvent(event: EngineEventsEnum): Observable<any> {
    return this.events[event];
  }

  fireEvent(event: EngineEventsEnum, params: any): void {
    Human.events.fire(event, params);
  }

  // set 'shown' attribute of specified nodes to specifed value and synchronize viewGraph
  toggleNodes(nodeIds: string[], visible: boolean) {
    // handles the conversion of view graph nodes to engine objects

    // Bind fetchAssets to visible, so that any time a node is set to visible the engine
    // will attempt to load it's assets (if they haven't been loaded already)
    const config = {replace: false, objects: {}, fetchAssets: visible };

    nodeIds.forEach((nodeId) => {
        this.getObjects(nodeId).forEach((humanObjectId: string) => {
          config.objects[humanObjectId] = visible;
        });
    });
    Human.scene.setEnabledObjects(config);
  }

  /**
  * Sets the specified nodes pickable state (including leaves) 
  */
  setNodesPickable(viewGraph: BdGraph<ViewTreeNode>, nodeIds: string[], pickable: boolean): BdGraphStateMap {
    // #TODO[AC]? The engine doesn't emit event of pick changes, track via state map
    const changeStateMap: BdGraphStateMap = {};

    nodeIds.forEach((nodeId) => {
      const object = Human.scene.objects[nodeId];
      // set node pickable, else leaves
      const pickableNodeIds = object.isLeaf() ? [nodeId] : this.getLeafObjects(nodeId);
      changeStateMap[nodeId] = { pickable: pickable };

      pickableNodeIds.forEach((pickableNodeId) => {
        const pickableObject = Human.scene.objects[pickableNodeId];
        pickableObject.setPickable(pickable);
        // track object state
        changeStateMap[pickableNodeId] = { pickable: pickable };
      });
    });

    return changeStateMap;
  }

  // build a BdGraph object representing the engine's scene
  createViewGraph(config: ChapterViewGraphConfig): BdGraph<ViewTreeNode> {
    // Create Synchronized Graph from Roots
    const viewGraph: BdGraph<ViewTreeNode> = GraphUtils.createGraphFromSrc(Human.scene.rootObjects, graphIdKey, graphChildKey, graphProperties);
    this.graphService.synchronize(viewGraph, graphPropertySync);

    // save a copy of the scene graph for future use
    this.sceneGraph = classToClass(viewGraph);

    let needsResync = false; // set to true when reconnecting;

    // ViewGraphConfig: build custom nodes
    config.nodes.forEach((nodeConfig) => {
      const customNode: ViewTreeNode = new ViewTreeNode(nodeConfig);
      customNode.label = nodeConfig.displayName;
      customNode.boolState.shown = true;
      this.graphService.addRootNode(viewGraph, customNode);
    });

    // ViewGraphConfig: reconnect nodes
    Object.entries(config.connect).forEach(([id, connections]) => {
      const removed = this.graphService.resetAsParent(viewGraph, id, connections.children, connections.replace);
      removed.forEach((rid) => {
        this.nodeToObjectMap[id] = [...this.nodeToObjectMap[id] || [], rid];
        this.objectToNodeMap[rid] = id;
      });
      needsResync = true; // this could change the state of viewgraph group nodes
    });

    // ViewGraphConfig: terminal nodes
    config.terminal.forEach((id) => {
      this.graphService.resetAsLeaf(viewGraph, id).forEach((removedId) => {
        this.objectToNodeMap[removedId] = id;
        this.nodeToObjectMap[id] = [...this.nodeToObjectMap[id] || [], removedId];
      }, this);
    }, this);

    // ViewGraphConfig: re-root nodes
    let viewRoots = config.root.children ? config.root.children : [];
    if (!config.root.replace) {
      Human.scene.rootObjects.forEach((humanRoot) => {
        viewRoots = [...viewRoots, ...humanRoot.objects.map((ho) => ho.objectId)];
      });
    }
    this.graphService.resetAsParent(viewGraph, null, viewRoots, true);

    // Full Sync after re-connection
    this.graphService.resetDepth(viewGraph);
    this.graphService.synchronize(viewGraph, graphPropertySync);
    if (needsResync) {this.graphService.synchronize(viewGraph, graphPropertySync); }

    return viewGraph;
  }

  // translate engine's object id to the representative view node
  getNode(viewGraph: BdGraph<ViewTreeNode>, id: string): ViewTreeNode {
    if (this.graphService.nodeExists(viewGraph, id)) {
      // object exists in view graph
      return GraphUtils.getNode(viewGraph, id);
    } else if (this.objectToNodeMap[id]) {
      // object is masked 
      return GraphUtils.getNode(viewGraph, this.objectToNodeMap[id]) as ViewTreeNode;
    } else {
      // no available node
      return null;
    }
  }

  // #TODO[EM]: this should be in state, harvested from chapter object on load
  // getChapterData
  getChapterGraphConfig(): ChapterViewGraphConfig {
    const c = Human.timeline.activeRoot._nowBranch;
    return new ChapterViewGraphConfig({...defaultGraphConfig, ...c.graph || {}});
  }

  getRootObjects(): any {
    // TODO.. type this
    return Human.scene.rootObjects;
  }

  getAllSceneObjects(): any {
    return Human.scene.objects;
  }

  // given a view node id, return the corresponding human object-id
  getObjects(id: string): string[] {
    if (this.nodeToObjectMap[id]) {
      return this.nodeToObjectMap[id];
    } else {
      return [Human.scene.objects[id].objectId] || [];
    }
    
  }

  // given a view node id, return the corresponding leaf human-object ids
  // #NOTE: [EM] This is stateful and could benefit from pre-caching viewNodeId -> human leaf objects
  getLeafObjects(id: string): string[] {
    if (id in this.nodeToObjectMap) {
      const leaves = {};
      this.nodeToObjectMap[id].forEach(objectId => {
        if (Human.scene.objects[objectId].isLeaf()) {
          leaves[objectId] = true;
        } else {
          Human.scene.getLeaves(objectId).forEach(leafId => {
            leaves[leafId] = true;
          });
        }
      });
      return Object.keys(leaves);
    } else if (id in Human.scene.objects) {
      return Human.scene.getLeaves(id);
    } else {
      return [];
    }
  }


  getChapter(id: string): any {
    return Human.timeline.branchTimelines[id] || null;
  }

  getActiveChapters(): any[] {
    if (Human.timeline.activeRoot) {
      return Human.timeline.activeRoot._chapters;
    } else {
      return [];
    }
  }
  
  // return bounding box of objects
  getBoundaryFromObjects(objectIds: string[]): any {
    objectIds = objectIds.filter((x) => Human.scene.objects[x].isLeaf());
    return Human.scene.getBoundary({objectIds: objectIds, clipped: true});
  }

  getLeaves(objectId: string) {
    return Human.scene.getLeaves(objectId);
  }

  // get visible leaves
  getVisibleLeaves(): string[] {
    return Human.scene.getEnabledLeaves();
  }

  // get camera lookat
  getCameraLookAt(): CameraModel {
    return plainToClass(CameraModel, Human.renderer.camera.getLookAt() as Object);
  }

  // get world position from barycentric coordiantes
  barycentricToWorld(objectId: string, primitiveIndex: number, barycentric: any, renderableIndex: number = 0): [number, number, number] {
    const out = new Float32Array([0, 0, 0 ]);
    const humanObject = Human.scene.objects[objectId];
    humanObject.barycentricToWorldSpace(renderableIndex, primitiveIndex, barycentric, out);
    return !isNaN(out[0]) ? [...Array.from(out)] as [number, number, number] : [0, 0, 0];
  }
  
  setGraphState(synchronization: SynchronizationModel) {
    Human.scene.setObjectProperties(synchronization);
  }

  // return a list of all states (namespaces) with assets loaded in the engine
  getAssetNamespaces(): string[] {
    const namespaces = {};
    Object.keys(Human.assets.loadedAssets).map(wholeId => wholeId.split('/')[0]).forEach(namespace => {
      namespaces[namespace] = true;
    });
    return Object.keys(namespaces);
  }

  // [SCENE QUERY] Return objects shown in at least one chapter
  queryShownObjects(chapters: ChapterModel[], wholeGraph = true): BdGraphPropertyValueMap {
    const shown = {};
    chapters.forEach(chapter => {
      // #NOTE: [EM] This could be extended to include otehr 'shown' conditions e.g. clipped, opacity, etc.
      const leafStateMap = calculateLeafStateMap(this.sceneGraph, chapter.synchronization, ['shown']);
      for (const id in leafStateMap) {
        if (leafStateMap.hasOwnProperty(id)) {
          shown[id] = (shown[id] || false) || leafStateMap[id].shown;
        }
      }
    });

    let result = shown;
    if (wholeGraph) {
      // create leaf state map from property maps
      const leafStateMap = this.graphService.createGraphStateMapFromPropertyMaps({'shown': shown});
      const graphStateMap = this.graphService.calculateGraphStateMap(this.sceneGraph, {'shown': 'trueWins'}, leafStateMap, true);
      result = this.graphService.extractPropertymapFromGraphState(graphStateMap, 'shown');
    }
    return result;
  }

  // load assets for graph objects if they exist 
  loadGraphObject(id: string): Observable<boolean> {
    const humanObjects = this.getObjects(id).map(objectId => Human.scene.objects[objectId]);

    return new Observable(observer => {
      // load default asset types
      Human.scene.graph.attachAssetSetsToSubtree(humanObjects, null, () => {
        observer.next(true);
        observer.complete();
      });
    });
  }
}
